import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Plus, Check, ChevronsUpDown } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AddLeadDialog = ({ onLeadAdded }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [companyOpen, setCompanyOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [companySearch, setCompanySearch] = useState("");
  const [contactSearch, setContactSearch] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    title: "",
    website: "",
    lead_notes: "",
    lead_date: ""
  });

  useEffect(() => {
    if (open) {
      fetchCompanies();
      fetchContacts();
    }
  }, [open]);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/companies`);
      setCompanies(response.data);
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/contacts`, {
        withCredentials: true
      });
      // Handle different API response structures
      const contactsData = Array.isArray(response.data) ? response.data : response.data.contacts || [];
      setContacts(contactsData);
      console.log('Fetched contacts:', contactsData.length);
    } catch (error) {
      console.error("Error fetching contacts:", error);
      setContacts([]);
    }
  };

  const handleContactSelect = (contact) => {
    setSelectedContact(contact);
    setFormData({
      ...formData,
      name: contact.name || formData.name,
      email: contact.email || "",
      phone: contact.phone || "",
      title: contact.title || ""
    });
    setContactOpen(false);
    setContactSearch("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error("Name is required");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/leads`, formData);
      toast.success("Lead added successfully!");
      setOpen(false);
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        title: ""
      });
      if (onLeadAdded) {
        onLeadAdded(response.data.id);
      }
    } catch (error) {
      console.error("Error adding lead:", error);
      toast.error("Failed to add lead");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          className="bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700"
          data-testid="add-lead-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Lead
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto" data-testid="add-lead-dialog">
        <DialogHeader>
          <DialogTitle>Add New Lead</DialogTitle>
          <DialogDescription>
            Enter the lead information below. Name is required.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Contact Name *</Label>
              <Popover open={contactOpen} onOpenChange={setContactOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={contactOpen}
                    className="w-full justify-between"
                    data-testid="lead-contact-dropdown"
                  >
                    {formData.name || "Select or type contact name..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <Command>
                    <CommandInput 
                      placeholder="Search or type new contact..." 
                      value={contactSearch}
                      onValueChange={setContactSearch}
                    />
                    <CommandList>
                      <CommandEmpty>
                        <div className="p-2">
                          <Button
                            variant="ghost"
                            className="w-full justify-start"
                            onClick={() => {
                              setFormData({...formData, name: contactSearch});
                              setSelectedContact(null);
                              setContactOpen(false);
                              setContactSearch("");
                            }}
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            Create new contact "{contactSearch}"
                          </Button>
                        </div>
                      </CommandEmpty>
                      <CommandGroup>
                        {contacts.map((contact) => (
                          <CommandItem
                            key={contact.id}
                            value={contact.name}
                            onSelect={() => handleContactSelect(contact)}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                selectedContact?.id === contact.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            <div className="flex flex-col">
                              <span>{contact.name}</span>
                              <span className="text-xs text-slate-500">
                                {contact.email || contact.phone || 'No contact info'}
                              </span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <p className="text-xs text-slate-500 mt-1">
                Select existing contact or type to create new
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="john@example.com"
                data-testid="lead-email-input"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="+1234567890"
                data-testid="lead-phone-input"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="company">Company</Label>
              <div className="flex gap-2">
                <Input
                  id="company"
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  placeholder="Type company name..."
                  data-testid="lead-company-input"
                  className="flex-1"
                />
                <Popover open={companyOpen} onOpenChange={setCompanyOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={companyOpen}
                      className="shrink-0"
                      data-testid="lead-company-dropdown"
                      type="button"
                    >
                      <ChevronsUpDown className="h-4 w-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="end">
                    <Command>
                      <CommandInput 
                        placeholder="Search companies..." 
                        value={companySearch}
                        onValueChange={setCompanySearch}
                      />
                      <CommandList>
                        <CommandEmpty>
                          <div className="p-2 text-sm text-slate-500">
                            No companies found. Type in the field to create new.
                          </div>
                        </CommandEmpty>
                        <CommandGroup>
                          {companies.map((company) => (
                            <CommandItem
                              key={company.id}
                              value={company.name}
                              onSelect={() => {
                                setFormData({...formData, company: company.name});
                                setCompanyOpen(false);
                                setCompanySearch("");
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  formData.company === company.name ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {company.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Type company name or select from dropdown
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="title">Job Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="Sales Manager"
                data-testid="lead-title-input"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="website">Website (Optional)</Label>
              <Input
                id="website"
                value={formData.website}
                onChange={(e) => setFormData({...formData, website: e.target.value})}
                placeholder="https://company.com"
                data-testid="lead-website-input"
              />
              <p className="text-xs text-slate-500">
                Company website helps improve enrichment accuracy
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="lead_date">Lead Date (Optional)</Label>
              <Input
                id="lead_date"
                type="date"
                value={formData.lead_date}
                onChange={(e) => setFormData({...formData, lead_date: e.target.value})}
                data-testid="lead-date-input"
              />
              <p className="text-xs text-slate-500">
                Optional — the date this lead was created/received
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="lead_notes">Lead Notes (Optional)</Label>
              <textarea
                id="lead_notes"
                value={formData.lead_notes}
                onChange={(e) => setFormData({...formData, lead_notes: e.target.value})}
                placeholder="Add any notes about this lead..."
                className="min-h-[100px] px-3 py-2 text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                data-testid="lead-notes-input"
              />
              <p className="text-xs text-slate-500">
                These notes will help AI better enrich this lead but won't be modified
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700"
              data-testid="submit-lead-btn"
            >
              {loading ? "Adding..." : "Add Lead"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddLeadDialog;
